import type { Data, Heading, Nodes, Parent, Table } from "mdast";
import type {} from "mdast-util-directive";
import { BILLING_CATEGORIES } from "@/lib/billing";

const HEADING_ID = /\s*\{#([\w-]+)\}$/;
const NOWRAP_COLUMN = /\s*\{\.nowrap\}$/;

type DirectiveKind = "containerDirective" | "leafDirective";

/** 利用ガイドで書けるディレクティブ。attributes は書かなければならない属性、children は中に書けるただ 1 つのディレクティブ。 */
const DIRECTIVES: Readonly<
	Record<string, { kind: DirectiveKind; attributes: readonly string[]; children?: string }>
> = {
	tabs: { kind: "containerDirective", attributes: [], children: "tab" },
	tab: { kind: "containerDirective", attributes: ["key", "label"] },
	examples: { kind: "containerDirective", attributes: [] },
	audience: { kind: "leafDirective", attributes: [] },
	next: { kind: "leafDirective", attributes: ["to"] },
	"billing-rates": { kind: "containerDirective", attributes: [], children: "category" },
	category: { kind: "containerDirective", attributes: ["id"] },
};

const BILLING_CATEGORY_IDS = new Set(BILLING_CATEGORIES.map((category) => category.id));

/**
 * 利用ガイドの Markdown 独自の書き方を、DocMarkdown が描ける形にする remark プラグイン。
 * - 見出し末尾の `{#id}` を外して見出しの id にする
 * - 表の見出しのセル末尾の `{.nowrap}` を外し、その列のセルを折り返さない印 (data-nowrap) を付ける。列の揃え (`:---:` など) は例外にする
 * - ディレクティブを data-doc に名前を持つ div にする。知らないブロックのディレクティブ、決めていない入れ子、
 *   単価に無いカテゴリは書き間違いなので例外にする
 * - 文中の `:名前` (Izanagi:Pro のような半角コロン) はディレクティブにせず、書いたとおりの文字列に戻す
 */
export function remarkDocSyntax() {
	return (tree: Nodes, file: { value: unknown }) => transform(tree, String(file.value));
}

function transform(node: Nodes, source: string): void {
	if (node.type === "heading") applyHeadingId(node);
	if (node.type === "table") {
		if (node.align?.some((align) => align !== null)) {
			throw new Error("利用ガイドの表では列の揃え (:--- など) を使えません");
		}
		applyNowrapColumns(node);
	}
	if (node.type === "containerDirective" || node.type === "leafDirective") {
		const spec = DIRECTIVES[node.name];
		if (spec?.kind !== node.type) {
			throw new Error(`利用ガイドで使えないディレクティブです: ${node.name} (${node.type})`);
		}
		const attributes = node.attributes ?? {};
		const missing = spec.attributes.filter((name) => !attributes[name]);
		if (missing.length > 0) {
			throw new Error(`ディレクティブ ${node.name} に属性 ${missing.join("・")} がありません`);
		}
		if (spec.children !== undefined) {
			const stray = (node.children as Nodes[]).find(
				(child) => child.type !== "containerDirective" || child.name !== spec.children,
			);
			if (stray) {
				throw new Error(
					`ディレクティブ ${node.name} の中には ${spec.children} だけを書けます (${stray.type})`,
				);
			}
		}
		if (node.name === "category" && !BILLING_CATEGORY_IDS.has(attributes.id ?? "")) {
			throw new Error(`単価のカテゴリに無い id です: ${attributes.id}`);
		}
		setHast(node, "div", {
			dataDoc: node.name,
			...Object.fromEntries(
				Object.entries(attributes).map(([name, value]) => [
					`data${name[0].toUpperCase()}${name.slice(1)}`,
					value ?? "",
				]),
			),
		});
	}
	if (!("children" in node)) return;
	const children = node.children as Nodes[];
	for (const [index, child] of children.entries()) {
		if (child.type === "textDirective") {
			children[index] = { type: "text", value: sourceOf(child, source) };
		} else {
			transform(child, source);
		}
	}
}

function sourceOf(node: Nodes, source: string): string {
	const start = node.position?.start.offset;
	const end = node.position?.end.offset;
	if (start === undefined || end === undefined) {
		throw new Error("利用ガイドの Markdown の位置を読めませんでした");
	}
	return source.slice(start, end);
}

function applyHeadingId(heading: Heading): void {
	const id = stripSuffix(heading, HEADING_ID);
	if (id !== null) setHast(heading, undefined, { id });
}

function applyNowrapColumns(table: Table): void {
	const [header, ...rows] = table.children;
	if (!header) return;
	for (const [column, cell] of header.children.entries()) {
		if (stripSuffix(cell, NOWRAP_COLUMN) === null) continue;
		for (const row of [header, ...rows]) {
			const target = row.children[column];
			if (target) setHast(target, undefined, { dataNowrap: "" });
		}
	}
}

/** 末尾の文字列ノードから pattern を外し、最初のグループ (無ければ一致全体) を返す。一致しなければ null。 */
function stripSuffix(parent: Parent, pattern: RegExp): string | null {
	const last = parent.children.at(-1);
	if (last?.type !== "text") return null;
	const match = pattern.exec(last.value);
	if (!match) return null;
	last.value = last.value.slice(0, match.index);
	return match[1] ?? match[0];
}

function setHast(node: { data?: Data }, hName: string | undefined, properties: object): void {
	const data = (node.data ??= {});
	if (hName) data.hName = hName;
	data.hProperties = { ...data.hProperties, ...properties };
}
